import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { OcupacionCentroDiaRow, OcupacionCentroHoraRow } from '../../../api/reports';
import { useCentros, type Centro } from '../../../hooks/useCentros';
import { useOcupacionCentroReport } from '../../../hooks/useReportJob';
import { downloadReportFile } from '../../../components/utils/downloadXlsx';

function dayCellsForMonth(month: string) {
  const start = dayjs(`${month}-01`);
  const daysInMonth = start.daysInMonth();
  const firstDow = (new Date(`${month}-01T00:00:00`).getDay() + 6) % 7;

  const cells: Array<number | null> = [];
  for (let i = 0; i < firstDow; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
  return cells;
}

function stateColor(state?: OcupacionCentroDiaRow['estado']) {
  switch (state) {
    case 'red':
      return '#d32f2f';
    case 'orange':
      return '#ef6c00';
    case 'yellow':
      return '#f9a825';
    case 'green':
      return '#2e7d32';
    default:
      return '#bdbdbd';
  }
}

function stateLabel(state?: OcupacionCentroDiaRow['estado']) {
  switch (state) {
    case 'red':
      return 'Rojo';
    case 'orange':
      return 'Naranja';
    case 'yellow':
      return 'Amarillo';
    case 'green':
      return 'Verde';
    default:
      return '-';
  }
}

function stateFromMaxPct(maxPct: number | undefined) {
  const value = maxPct ?? 0;
  if (value >= 100) return 'red' as const;
  if (value > 90) return 'orange' as const;
  if (value > 70) return 'yellow' as const;
  return 'green' as const;
}

export default function OcupacionCentroPage() {
  const { t } = useTranslation<'translation'>();

  const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
  const [centroSel, setCentroSel] = useState<Centro | null>(null);
  const [jobId, setJobId] = useState<string>();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedHour, setSelectedHour] = useState<number | null>(null);

  const { rows: centros } = useCentros();
  const { run, useStatus } = useOcupacionCentroReport();
  const { data: status } = useStatus(jobId);

  const desde = `${month}-01`;
  const hasta = dayjs(desde).endOf('month').format('YYYY-MM-DD');

  const rowsDaily = status?.result?.rowsDaily ?? [];
  const rowsHourly = status?.result?.rowsHourly ?? [];

  const byDate = useMemo(() => {
    const map = new Map<string, OcupacionCentroDiaRow>();
    rowsDaily.forEach((r) => map.set(r.fecha, r));
    return map;
  }, [rowsDaily]);

  const criticalByDate = useMemo(() => {
    const map = new Map<string, number>();
    rowsHourly.forEach((r) => {
      const isCritical = r.pct > 70;
      if (!isCritical) return;
      map.set(r.fecha, (map.get(r.fecha) ?? 0) + 1);
    });
    return map;
  }, [rowsHourly]);

  const hourlyForSelected = useMemo(() => {
    if (!selectedDate) return [] as OcupacionCentroHoraRow[];
    return rowsHourly.filter((r) => r.fecha === selectedDate).sort((a, b) => a.hora - b.hora);
  }, [rowsHourly, selectedDate]);

  const selectedHourRow = useMemo(() => {
    if (selectedHour == null) return null;
    return hourlyForSelected.find((r) => r.hora === selectedHour) ?? null;
  }, [hourlyForSelected, selectedHour]);

  const onGenerate = async () => {
    if (!centroSel?.id) return;
    const result = await run({ desde, hasta, centroId: centroSel.id });
    setJobId(result.jobId);
    setSelectedDate(null);
    setSelectedHour(null);
  };

  const onDownload = async () => {
    if (!status?.result?.url) return;
    await downloadReportFile(status.result.url);
  };

  const cells = dayCellsForMonth(month);

  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t('ocupacionCentro.titulo')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('ocupacionCentro.ayuda')}
      </Typography>

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
          <Autocomplete
            options={centros.filter((c) => c.nombre !== 'OTHER')}
            getOptionLabel={(o) => o.nombre}
            value={centroSel}
            onChange={(_, v) => setCentroSel(v)}
            renderInput={(params) => (
              <TextField {...params} size="small" label={t('filters.centro')} />
            )}
            sx={{ width: 280 }}
          />

          <TextField
            type="month"
            size="small"
            label={t('ocupacionCentro.filtros.mes')}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <Button variant="contained" onClick={onGenerate} disabled={!centroSel?.id}>
            {t('ocupacionCentro.filtros.generar')}
          </Button>

          {status?.done && status?.result?.url ? (
            <Button variant="text" onClick={onDownload}>
              {t('ocupacionCentro.filtros.descargar')}
            </Button>
          ) : null}
        </Stack>
      </Paper>

      {!centroSel?.id ? (
        <Alert severity="info">{t('ocupacionCentro.estado.seleccionaCentro')}</Alert>
      ) : null}

      {status && !status.done && !status.failed ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={22} />
          <Typography>{t('ocupacionCentro.estado.generando')}</Typography>
        </Stack>
      ) : null}

      {status?.done ? (
        <>
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={t('ocupacionCentro.leyenda.rojo')}
                sx={{ bgcolor: '#d32f2f', color: '#fff' }}
              />
              <Chip
                label={t('ocupacionCentro.leyenda.naranja')}
                sx={{ bgcolor: '#ef6c00', color: '#fff' }}
              />
              <Chip
                label={t('ocupacionCentro.leyenda.amarillo')}
                sx={{ bgcolor: '#f9a825', color: '#000' }}
              />
              <Chip
                label={t('ocupacionCentro.leyenda.verde')}
                sx={{ bgcolor: '#2e7d32', color: '#fff' }}
              />
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={1} columns={7}>
              {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((d) => (
                <Grid item xs={1} key={d}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {d}
                  </Typography>
                </Grid>
              ))}

              {cells.map((d, idx) => {
                if (!d) {
                  return (
                    <Grid item xs={1} key={`e-${idx}`}>
                      <Box sx={{ height: 72 }} />
                    </Grid>
                  );
                }

                const date = `${month}-${String(d).padStart(2, '0')}`;
                const row = byDate.get(date);
                const active = selectedDate === date;
                const state = stateFromMaxPct(row?.maxPct);
                const bg = stateColor(state);
                const criticalSlots = criticalByDate.get(date) ?? 0;

                return (
                  <Grid item xs={1} key={date}>
                    <Button
                      fullWidth
                      variant={active ? 'contained' : 'outlined'}
                      onClick={() => {
                        setSelectedDate(date);
                        setSelectedHour(null);
                      }}
                      sx={{
                        minHeight: 72,
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                        flexDirection: 'column',
                        p: 1,
                        bgcolor: row ? bg : undefined,
                        color: row ? (state === 'yellow' ? '#111' : '#fff') : undefined,
                        borderColor: active ? '#0d47a1' : undefined,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ lineHeight: 1 }}>
                        {d}
                      </Typography>
                      <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
                        {row ? `${Math.round(row.maxPct)}%` : '-'}
                      </Typography>
                      <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
                        {stateLabel(state)}
                      </Typography>
                      <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
                        {criticalSlots}/24
                      </Typography>
                    </Button>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>

          {selectedDate ? (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t('ocupacionCentro.detalle.titulo', { fecha: selectedDate })}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('ocupacionCentro.detalle.ayuda')}
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip
                  label={t('ocupacionCentro.detalle.ocupacionMax', {
                    pct: Math.round(byDate.get(selectedDate)?.maxPct ?? 0),
                  })}
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={t('ocupacionCentro.detalle.franjasCriticas', {
                    n: criticalByDate.get(selectedDate) ?? 0,
                  })}
                  color="warning"
                  variant="outlined"
                />
              </Stack>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('ocupacionCentro.tabla.hora')}</TableCell>
                    <TableCell>{t('ocupacionCentro.tabla.total')}</TableCell>
                    <TableCell>{t('ocupacionCentro.tabla.ocupadas')}</TableCell>
                    <TableCell>{t('ocupacionCentro.tabla.bloqueadas')}</TableCell>
                    <TableCell>{t('ocupacionCentro.tabla.disponibles')}</TableCell>
                    <TableCell>{t('ocupacionCentro.tabla.pct')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hourlyForSelected.map((r) => (
                    <TableRow
                      key={`${r.fecha}-${r.hora}`}
                      hover
                      selected={selectedHour === r.hora}
                      onClick={() => setSelectedHour(r.hora)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{String(r.hora).padStart(2, '0')}:00</TableCell>
                      <TableCell>{r.totalAulas}</TableCell>
                      <TableCell>{r.aulasOcupadas}</TableCell>
                      <TableCell>{r.aulasBloqueadas}</TableCell>
                      <TableCell>{r.aulasDisponibles}</TableCell>
                      <TableCell>{r.pct.toFixed(1)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t('ocupacionCentro.detalle.horaSeleccionada')}
              </Typography>

              {selectedHourRow ? (
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {t('ocupacionCentro.detalle.aulasOcupadasTitulo', {
                          hora: `${String(selectedHourRow.hora).padStart(2, '0')}:00`,
                          n: selectedHourRow.aulasOcupadasDetalle?.length ?? 0,
                        })}
                      </Typography>
                      <Stack spacing={0.5}>
                        {(selectedHourRow.aulasOcupadasDetalle ?? []).map((a) => (
                          <Typography key={a.aulaId} variant="body2">
                            {a.aula}
                          </Typography>
                        ))}
                        {(selectedHourRow.aulasOcupadasDetalle?.length ?? 0) === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            {t('ocupacionCentro.detalle.sinAulas')}
                          </Typography>
                        ) : null}
                      </Stack>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {t('ocupacionCentro.detalle.aulasDisponiblesTitulo', {
                          hora: `${String(selectedHourRow.hora).padStart(2, '0')}:00`,
                          n: selectedHourRow.aulasDisponiblesDetalle?.length ?? 0,
                        })}
                      </Typography>
                      <Stack spacing={0.5}>
                        {(selectedHourRow.aulasDisponiblesDetalle ?? []).map((a) => (
                          <Typography key={a.aulaId} variant="body2">
                            {a.aula}
                          </Typography>
                        ))}
                        {(selectedHourRow.aulasDisponiblesDetalle?.length ?? 0) === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            {t('ocupacionCentro.detalle.sinAulas')}
                          </Typography>
                        ) : null}
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              ) : null}
            </Paper>
          ) : null}
        </>
      ) : null}
    </Stack>
  );
}
